import FunnelLogo from '@/components/v1/FunnelLogo'
import PaywallFaq from '@/components/v1/PaywallFaq'
import PaywallGuarantee from '@/components/v1/PaywallGuarantee'
import PaywallPlans from '@/components/v1/PaywallPlans'
import {
  getAvailablePlans,
  type PaywallPlan,
} from '@/components/v1/paywallPricing'
import PaywallReviews from '@/components/v1/PaywallReviews'
import PaywallSocialProof from '@/components/v1/PaywallSocialProof'
import PaywallTimerBar from '@/components/v1/PaywallTimerBar'
import useCountdown from '@/components/v1/useCountdown'
import WhatYouGet from '@/components/v1/WhatYouGet'
import { type GetPricesData } from '@app-studio-web/ui/loaders/getPricesLoader'
import ErrorPage from '@app-studio-web/ui/pages/ErrorPage'
import LoadingPage from '@app-studio-web/ui/pages/LoadingPage'
import { type Plan } from '@app-studio-web/ui/types'
import { Suspense, useRef, useState } from 'react'
import { Trans } from 'react-i18next'
import { Await, useLoaderData } from 'react-router-dom'

const COUNTDOWN_MINUTES = 9
const POPULAR_PLAN: Plan = 'top'

function Paywall({
  data,
  paywallPlans,
}: {
  data: GetPricesData
  paywallPlans: PaywallPlan[]
}) {
  const popularPlan = paywallPlans.some(({ plan }) => plan === POPULAR_PLAN)
    ? POPULAR_PLAN
    : paywallPlans[0].plan

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [selectedPlan, setSelectedPlan] = useState<Plan>(popularPlan)
  const plansRef = useRef<HTMLDivElement>(null)
  const { isExpired, timeLeft } = useCountdown(COUNTDOWN_MINUTES)

  const selectedPaywallPlan =
    paywallPlans.find(({ plan }) => plan === selectedPlan) ?? paywallPlans[0]

  if (isSubmitting) return <LoadingPage />

  const plans = (
    <PaywallPlans
      currency={data.currency}
      isSubmitting={isSubmitting}
      onPlanChange={setSelectedPlan}
      paywallPlans={paywallPlans}
      popularPlan={popularPlan}
      selectedPaywallPlan={selectedPaywallPlan}
      setIsSubmitting={setIsSubmitting}
    />
  )

  return (
    <>
      {isExpired ? (
        <header className="hw-container flex h-16 items-center justify-center">
          <FunnelLogo />
        </header>
      ) : (
        <PaywallTimerBar
          onGetPlan={() =>
            plansRef.current?.scrollIntoView({
              behavior: 'smooth',
              block: 'start',
            })
          }
          timeLeft={timeLeft}
        />
      )}

      <div className="hw-container space-y-10 pt-6 pb-16">
        <section className="space-y-5">
          <h1 className="hw-heading">
            <Trans
              components={{ colored: <span className="text-hw-primary" /> }}
              i18nKey="pages.checkout.plans.heading"
            />
          </h1>

          <PaywallSocialProof />
        </section>

        <section className="scroll-mt-24" ref={plansRef}>
          {plans}
        </section>

        <section>
          <WhatYouGet />
        </section>

        <section>
          <PaywallReviews />
        </section>

        <section>
          <PaywallGuarantee />
        </section>

        <section>
          <PaywallFaq />
        </section>

        <section>{plans}</section>
      </div>
    </>
  )
}

export default function PlansPage() {
  const data = useLoaderData() as GetPricesData

  return (
    <div className="bg-hw-cream text-hw-ink min-h-dvh">
      <Suspense fallback={<LoadingPage />}>
        <Await errorElement={<ErrorPage />} resolve={data}>
          {(getPricesData: GetPricesData) => {
            const paywallPlans = getAvailablePlans(getPricesData)

            return paywallPlans.length > 0 ? (
              <Paywall data={getPricesData} paywallPlans={paywallPlans} />
            ) : (
              <ErrorPage />
            )
          }}
        </Await>
      </Suspense>
    </div>
  )
}
